import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PlusCircle, X } from 'react-feather';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import { ChatContextType, MessageGenStateSchema } from '@trinity/chat';
import { AuraChatContainer } from '@trinity/chat-components/components/BrandInsights/AuraChat.Container.js';
import { ChatContainer } from '@trinity/chat-components/components/BrandInsights/Chat.Container.js';
import {
  ChatContextSelectorVisibleContext,
  ChatHomeButtonVisibleContext,
  ChatViewType,
  ChatViewTypeContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context.js';
import { LinkStateContainer } from '@trinity/chat-components/components/BrandInsights/Link.Container.js';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat.js';
import {
  ChatInputFeaturesContext,
  ChatLandingCardsVisibleContext,
  ChatPageContextDataContext,
  DispatchPageContextDataContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { ChatHeader } from '@trinity/chat-components/components/Chat.Header/index.js';
import { ChatStateContainer } from '@trinity/chat-components/components/Chat.State/Chat.State.js';
import { ChatPendingQueryContextContainer } from '@trinity/chat-components/components/Chat.State/ChatPendingQueryContextContainer.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context.js';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { useAppSelector } from '@/store';
import { MessageGenerationStateContext } from '@/screens/MessageGenSession/Context';

const HACKATHON_AGENT_IDENTIFIER = 'hackathon-agent';
const MESSAGE_GENERATION_AGENT_IDENTIFIER = 'message-generation-agent';

function buildAgentContext(
  item: ChatContextItem,
  sessionId: string | null,
  segmentIdentifier: string | null,
  runName: string | null,
  messageGenSessionId: number | null
): BrandInsightsPageContext.Agent {
  // For all agents, pass sessionId and optionally segmentIdentifier in metadata
  // Also pass runName to display in the chat header
  return {
    type: ChatContextType.Agent,
    data: item,
    metadata: {
      ...(sessionId ? { sessionId } : {}),
      ...(segmentIdentifier ? { segmentIdentifier } : {}),
      ...(runName ? { runName } : {}),
      ...(messageGenSessionId ? { messageGenSessionId } : {}),
    },
  };
}

type SegmentProfileSummary = {
  name: string;
  size: number;
  pct: number;
  profile_narrative?: string;
  commercial_action?: string;
  archetype_distribution?: Record<string, number>;
  momentum_distribution?: Record<string, number>;
  distinguishing_features?: { name: string; z_score: number }[];
  top_molecules?: { molecule: string; mean_share: number }[];
};

const noop: Dispatch<SetStateAction<boolean>> = () => {};

export function ChatInterfaceNative({
  onClose,
  segmentIdentifier,
  segmentationRunId,
  runName,
  disabled,
  agentIdentifier,
}: {
  agentIdentifier?: string;
  onClose?: () => void;
  segmentIdentifier?: string | null;
  segmentProfile?: SegmentProfileSummary | null;
  segmentationRunId?: number | null;
  runName?: string | null;
  disabled?: boolean;
}): React.JSX.Element {
  const brandIdentifier = useActiveBrandIdentifier();
  const apiClient = useChatApiClient();

  const { data: agents } = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier],
    queryFn: () => apiClient.chat.getChatContexts({ brandIdentifier }).then((res) => res.items),
    enabled: !!brandIdentifier,
  });

  const messageGenSession = useContext(MessageGenerationStateContext);

  const [segmentationSessionId, setSegmentationSessionId] = useState<string | null>(null);

  const selectedAgentItem = useMemo(() => {
    if (!agents?.length) return null;

    if (agentIdentifier === MESSAGE_GENERATION_AGENT_IDENTIFIER) {
      // Check for message generation agent first
      const messageGenAgent = agents.find((a) =>
        a.agent.identifier.startsWith(MESSAGE_GENERATION_AGENT_IDENTIFIER)
      );
      if (messageGenAgent) return messageGenAgent;
    }

    // Fall back to hackathon agent
    return agents.find((a) => a.agent.identifier.startsWith(HACKATHON_AGENT_IDENTIFIER)) ?? null;
  }, [agents, agentIdentifier]);

  // Fetch segmentation run to get sessionId (for Segment Profile tab usage)
  useEffect(() => {
    if (!segmentationRunId) return;
    if (segmentationSessionId) return; // already fetched

    apiClient.authenticatedClient
      .get<{ run: { sessionId: string | null } }>(`/segmentation-runs/${segmentationRunId}`)
      .then((response) => {
        if (response.data.run.sessionId) {
          setSegmentationSessionId(response.data.run.sessionId);
        }
      })
      .catch((error) => {
        console.error('[ChatInterfaceNative] Failed to fetch segmentation run:', error);
      });
  }, [segmentationRunId, segmentationSessionId, apiClient]);

  const resolvedAgent = useMemo(() => {
    if (!selectedAgentItem) return null;

    return buildAgentContext(
      selectedAgentItem,
      segmentationSessionId,
      segmentIdentifier ?? null,
      runName ?? null,
      messageGenSession?.id ?? null
    );
  }, [selectedAgentItem, segmentIdentifier, runName, segmentationSessionId, messageGenSession?.id]);

  const [contextData, setContextData] = useState<BrandInsightsPageContext.Item | null>(resolvedAgent);
  const [prevResolvedAgent, setPrevResolvedAgent] = useState(resolvedAgent);
  if (resolvedAgent !== prevResolvedAgent) {
    setPrevResolvedAgent(resolvedAgent);
    if (resolvedAgent) {
      setContextData(resolvedAgent);
    }
  }

  const canToggleAuraChat = useAppSelector((s) => s.features.chatAuraIframesEnabled);

  const sidePanelExpandedValue = useMemo<[boolean, Dispatch<SetStateAction<boolean>>]>(
    () => [true, noop],
    []
  );

  const inputFeaturesValue = useMemo(() => ({ fileUpload: false, voiceInput: true }) as const, []);

  if (!contextData) {
    if (resolvedAgent) {
      setContextData(resolvedAgent);
    }
    return null;
  }

  if (disabled) {
    return (
      <DisabledContainer>
        <DisabledOverlay>
          <DisabledIcon>🎯</DisabledIcon>
          <DisabledTitle>Select a Segmentation Run</DisabledTitle>
          <DisabledText>
            Select a segmentation run from the panel on the right to begin generating messages
          </DisabledText>
        </DisabledOverlay>
      </DisabledContainer>
    );
  }

  return (
    <AuraChatContainer canToggleAuraChat={canToggleAuraChat}>
      <ChatViewTypeContext.Provider value={ChatViewType.Page}>
        <DispatchPageContextDataContext.Provider value={setContextData}>
          <ChatPageContextDataContext.Provider value={contextData}>
            <ChatStateContainer>
              <ChatPendingQueryContextContainer>
                <LinkStateContainer>
                  <ChatContainer>
                    <ChatInputFeaturesContext.Provider value={inputFeaturesValue}>
                      <ChatLandingCardsVisibleContext.Provider value={false}>
                        <SidePanelExpandedContext.Provider value={sidePanelExpandedValue}>
                          <ChatHomeButtonVisibleContext.Provider value={false}>
                            <ChatContextSelectorVisibleContext.Provider value={false}>
                              <PageHeader>
                                <StyledAutoAwesomeIcon />
                                <PageHeaderTitle>Discover Insights</PageHeaderTitle>
                                {onClose && (
                                  <CloseBtn onClick={onClose} title="Hide chat">
                                    <X size={16} />
                                  </CloseBtn>
                                )}
                              </PageHeader>
                              <HeaderRow>
                                <ChatHeader showContextDisplayOverride={false} />
                                <NewChatButton agentContext={contextData} />
                              </HeaderRow>
                              <ChatInstance isSidePanel />
                            </ChatContextSelectorVisibleContext.Provider>
                          </ChatHomeButtonVisibleContext.Provider>
                        </SidePanelExpandedContext.Provider>
                      </ChatLandingCardsVisibleContext.Provider>
                    </ChatInputFeaturesContext.Provider>
                  </ChatContainer>
                </LinkStateContainer>
              </ChatPendingQueryContextContainer>
            </ChatStateContainer>
          </ChatPageContextDataContext.Provider>
        </DispatchPageContextDataContext.Provider>
      </ChatViewTypeContext.Provider>
    </AuraChatContainer>
  );
}

function NewChatButton({ agentContext }: { agentContext: BrandInsightsPageContext.Item }): React.JSX.Element {
  const dispatch = useContext(ChatStateDispatchContext);
  const pageContext = useContext(ChatPageContextDataContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const handleNewChat = useCallback(() => {
    const context = pageContext ?? agentContext;
    const resetLandingCards =
      context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(context, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [dispatch, pageContext, agentContext, brandIdentifier]);

  return (
    <Tooltip title="New Chat">
      <StyledNewChatButton onClick={handleNewChat}>
        <PlusCircle size={20} />
      </StyledNewChatButton>
    </Tooltip>
  );
}

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const PageHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 8px 0',
  marginBottom: '8px',
});

const PageHeaderTitle = styled.span({
  fontSize: 16,
  fontWeight: 600,
  color: '#1a1a1a',
  flex: 1,
});

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  '& > *:first-child': {
    flex: 1,
    minWidth: 0,
  },
});

const CloseBtn = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: theme.palette.gray[400],
  padding: '4px',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.gray[100],
    color: theme.palette.gray[700],
  },
}));

const StyledNewChatButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '6px',
  marginRight: '8px',
  borderRadius: '4px',
  color: theme.palette.primary.main,
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));

const DisabledContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  background: 'rgba(248, 249, 250, 0.95)',
});

const DisabledOverlay = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '40px',
  maxWidth: '400px',
});

const DisabledIcon = styled.div({
  fontSize: '48px',
  marginBottom: '16px',
});

const DisabledTitle = styled.div({
  fontSize: '18px',
  fontWeight: 600,
  color: '#323232',
  marginBottom: '8px',
});

const DisabledText = styled.div({
  fontSize: '14px',
  color: '#626262',
  lineHeight: 1.5,
});
